import { NAME_MAX, NAME_MIN } from '@sunderfen/shared';
import { devLogin, listWorlds, loadMap, me, serverConfig, session, signup, socketUrl, type ServerConfig, type WorldInfo } from './api.js';
import { Game } from './game.js';
import { Connection } from './net.js';
import { audio } from './audio/engine.js';
import { rotateTips } from './ui/tips.js';

const $ = <T extends HTMLElement = HTMLElement>(s: string) => document.querySelector(s) as T;
function show(screen: 'login' | 'worlds' | 'game') {
  for (const s of ['login', 'worlds', 'game']) $(`#screen-${s}`).hidden = s !== screen;
}

/* ---------------- login ---------------- */
const nameInput = $<HTMLInputElement>('#login-name');
nameInput.minLength = NAME_MIN; nameInput.maxLength = NAME_MAX;
nameInput.value = session.get('name') ?? '';
rotateTips($('#login-tip'));
const LOGOS: Record<string, string> = {
  google: '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="#4285F4" d="M22.6 12.2c0-.8-.1-1.5-.2-2.2H12v4.2h5.9a5 5 0 0 1-2.2 3.3v2.7h3.6c2.1-1.9 3.3-4.8 3.3-8z"/><path fill="#34A853" d="M12 23c3 0 5.5-1 7.3-2.7l-3.6-2.8c-1 .7-2.2 1.1-3.7 1.1-2.9 0-5.3-1.9-6.2-4.5H2.1v2.9A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.8 14.1a6.6 6.6 0 0 1 0-4.2V7H2.1a11 11 0 0 0 0 10z"/><path fill="#EA4335" d="M12 5.4c1.6 0 3.1.6 4.2 1.7l3.2-3.2A11 11 0 0 0 2.1 7l3.7 2.9C6.7 7.3 9.1 5.4 12 5.4z"/></svg>',
  discord: '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="#5865F2" d="M20.3 4.4A19.8 19.8 0 0 0 15.4 3l-.6 1.3a18.4 18.4 0 0 0-5.5 0L8.6 3a19.7 19.7 0 0 0-4.9 1.5C.6 9.1-.3 13.6.1 18.1a19.9 19.9 0 0 0 6 3l1.3-2a12.9 12.9 0 0 1-2-1l.5-.4a14.2 14.2 0 0 0 12.2 0l.5.4c-.6.4-1.3.7-2 1l1.3 2a19.8 19.8 0 0 0 6-3c.5-5.2-.8-9.7-3.6-13.7zM8 15.4c-1.2 0-2.2-1.1-2.2-2.4S6.8 10.6 8 10.6s2.2 1.1 2.2 2.4-1 2.4-2.2 2.4zm8 0c-1.2 0-2.2-1.1-2.2-2.4s1-2.4 2.2-2.4 2.2 1.1 2.2 2.4-1 2.4-2.2 2.4z"/></svg>',
};
let cfg: ServerConfig = { devLogin: true, providers: [] };
const cfgReady = serverConfig().then(c => {
  cfg = c;
  const box = $('#login-providers');
  box.replaceChildren(...c.providers.map(p => {
    const a = document.createElement('a');
    a.className = `btn provider ${p.id}`; a.href = `/auth/${p.id}/start`;
    a.innerHTML = `${LOGOS[p.id] ?? ''}<span></span>`; a.querySelector('span')!.textContent = `Sign in with ${p.name}`;
    a.addEventListener('click', () => audio.unlock());
    return a;
  }));
  box.hidden = !c.providers.length;
  $('#login-dev').hidden = !c.devLogin;
  $('#login-or').hidden = !(c.devLogin && c.providers.length);
  nameInput.required = c.devLogin;
});
function loginError(msg: string) { const err = $('#login-err'); err.textContent = msg; err.hidden = false; }

/* ---------------- choosing a name (first sign-in with Google / Discord) ---------------- */
const signupName = $<HTMLInputElement>('#signup-name');
signupName.minLength = NAME_MIN; signupName.maxLength = NAME_MAX;
function showSignup(via: string) {
  show('login');
  $('#login-form').hidden = true; $('#signup-form').hidden = false;
  const name = cfg.providers.find(p => p.id === via)?.name ?? (via === 'google' ? 'Google' : via === 'discord' ? 'Discord' : '');
  $('#signup-via').textContent = `You're signed in${name ? ` with ${name}` : ''}. Pick the name people will see over your head. It's yours for good.`;
  signupName.focus();
}
$('#signup-form').addEventListener('submit', async e => {
  e.preventDefault();
  audio.unlock();
  const err = $('#signup-err'), btn = $<HTMLButtonElement>('#signup-go'), ticket = session.get('signup');
  err.hidden = true;
  if (!ticket) { $('#signup-form').hidden = true; $('#login-form').hidden = false; loginError('That sign-up expired. Sign in again.'); return; }
  btn.disabled = true;
  try {
    const { token, name } = await signup(ticket, signupName.value.trim());
    session.del('signup'); session.set('token', token); session.set('name', name);
    $('#signup-form').hidden = true; $('#login-form').hidden = false;
    await showWorlds();
  } catch (x) { err.textContent = (x as Error).message; err.hidden = false; }
  finally { btn.disabled = false; }
});
$('#signup-back').addEventListener('click', () => { session.del('signup'); $('#signup-form').hidden = true; $('#login-form').hidden = false; });

$('#login-form').addEventListener('submit', async e => {
  e.preventDefault();
  audio.unlock();
  if (!cfg.devLogin) return;
  const err = $('#login-err'), btn = $<HTMLButtonElement>('#login-go');
  err.hidden = true; btn.disabled = true;
  try {
    const { token, name } = await devLogin(nameInput.value.trim());
    session.set('token', token); session.set('name', name);
    await showWorlds();
  } catch (x) { err.textContent = (x as Error).message; err.hidden = false; }
  finally { btn.disabled = false; }
});

/* ---------------- world list ---------------- */
async function showWorlds(message?: string) {
  show('worlds');
  $('#worlds-who').textContent = `Playing as ${session.get('name') ?? ''}`;
  const err = $('#worlds-err');
  err.hidden = !message; err.textContent = message ?? '';
  const list = $('#world-list');
  list.innerHTML = '<p class="muted">Loading worlds…</p>';
  let worlds: WorldInfo[];
  try { worlds = await listWorlds(); } catch (x) { list.innerHTML = ''; err.textContent = `Could not reach the server: ${(x as Error).message}`; err.hidden = false; return; }
  list.innerHTML = '';
  void showSignins();
  for (const w of worlds) {
    const b = document.createElement('button');
    b.type = 'button'; b.className = 'world'; b.disabled = !w.online || w.population >= w.capacity;
    const pct = Math.round((w.population / Math.max(1, w.capacity)) * 100);
    b.innerHTML = `<span class="wn">World ${w.id}<small></small></span><span class="wl"></span><span class="pop"><i style="width:${pct}%"></i></span><span class="ws"></span>`;
    b.querySelector('small')!.textContent = w.name;
    b.querySelector('.wl')!.textContent = w.location;
    b.querySelector('.ws')!.textContent = !w.online ? 'Offline' : w.population >= w.capacity ? 'Full' : `${w.population} of ${w.capacity} players`;
    b.onclick = () => enterWorld(w);
    list.appendChild(b);
  }
}
/** Which sign-ins this character has, and buttons to add the others. */
async function showSignins() {
  const box = $('#worlds-signins'), token = session.get('token');
  await cfgReady;
  if (!token || !cfg.providers.length) { box.hidden = true; return; }
  let info;
  try { info = await me(token); } catch { box.hidden = true; return; }
  box.replaceChildren();
  const label = document.createElement('span'); label.className = 'muted'; label.textContent = 'Sign in with:'; box.appendChild(label);
  for (const p of info.providers) {
    if (info.signins.includes(p.id)) {
      const s = document.createElement('span'); s.className = `linked ${p.id}`; s.innerHTML = `${LOGOS[p.id] ?? ''}<span></span>`; s.querySelector('span')!.textContent = `${p.name} ✓`; box.appendChild(s);
    } else {
      const a = document.createElement('a'); a.className = `btn small provider ${p.id}`; a.href = `/auth/${p.id}/start?link=${encodeURIComponent(token)}`;
      a.innerHTML = `${LOGOS[p.id] ?? ''}<span></span>`; a.querySelector('span')!.textContent = `Link ${p.name}`;
      a.title = `Sign in with ${p.name} too, so either one gets you this character`; box.appendChild(a);
    }
  }
  box.hidden = false;
}
$('#worlds-refresh').addEventListener('click', () => void showWorlds());
$('#worlds-logout').addEventListener('click', () => { session.del('token'); show('login'); nameInput.focus(); });

/* ---------------- game ---------------- */
async function enterWorld(w: WorldInfo) {
  audio.unlock();
  const token = session.get('token');
  if (!token) { show('login'); return; }
  session.set('world', String(w.id));
  const map = await loadMap(w.map);
  show('game');
  new Game(map, w, new Connection(socketUrl(w), token), ({ reason, kicked }) => {
    // A fresh page is the simplest way to drop every GPU resource and listener between sessions.
    session.set('flash', reason);
    session.set('retry', kicked ? '' : String(w.id));
    location.reload();
  });
}

/* ---------------- boot ---------------- */
/** Coming back from Google or Discord: the server put the outcome in the address fragment. */
function readReturn(): { error?: string; linked?: string; signup?: string; via?: string } | null {
  if (!location.hash.startsWith('#')) return null;
  const q = new URLSearchParams(location.hash.slice(1));
  if (!['login', 'signup', 'error', 'linked'].some(k => q.has(k))) return null;
  history.replaceState(null, '', location.pathname + location.search); // keep tokens out of history and bookmarks
  const login = q.get('login');
  if (login) {
    session.set('token', login);
    try { session.set('name', JSON.parse(atob(login.split('.')[0].replace(/-/g, '+').replace(/_/g, '/'))).name); } catch { /* shown after /api/me */ }
  }
  return { error: q.get('error') ?? undefined, linked: q.get('linked') ?? undefined, signup: q.get('signup') ?? undefined, via: q.get('via') ?? undefined };
}

(async () => {
  const back = readReturn();
  await cfgReady;
  if (back?.signup) { session.set('signup', back.signup); showSignup(back.via ?? ''); return; }
  if (back?.error && !session.get('token')) { show('login'); loginError(back.error); return; }
  if (back?.error || back?.linked) { await showWorlds(back.error ?? `${cfg.providers.find(p => p.id === back.linked)?.name ?? 'That'} sign-in is linked. Either one gets you this character now.`); return; }
  const flash = session.get('flash'), retry = session.get('retry');
  session.del('flash'); session.del('retry');
  if (!session.get('token')) { show('login'); nameInput.focus(); return; }
  if (retry && flash) {
    show('game');
    $('#conn-lost').hidden = false;
    $('#conn-text').textContent = flash;
    $('#conn-retry').onclick = async () => { const w = (await listWorlds()).find(x => x.id === Number(retry)); $('#conn-lost').hidden = true; if (w?.online) void enterWorld(w); else void showWorlds('That world is offline right now.'); };
    $('#conn-worlds').onclick = () => { $('#conn-lost').hidden = true; void showWorlds(); };
    return;
  }
  await showWorlds(flash ?? undefined);
})();
